import { Html } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import { Button } from "@/components/ui/button";
import { Plus, Minus } from "lucide-react";
import { useShelvingStore } from "@/lib/shelving-store";
import { CM_TO_M } from "@/lib/utils";
import * as THREE from "three";
import { useEffect } from "react";

const FRAME_WIDTH = 3; // in cm
const INITIAL_LOOK_TARGET = new THREE.Vector3(1.2, 0.98, -0.12);

const plusButtonStyle = {
  backgroundColor: 'white',
  color: '#212b4f',
  border: '2px solid #212b4f',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
}

const minusButtonStyle = {
  backgroundColor: '#212b4f',
  color: 'white',
  border: '2px solid #212b4f',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
}

export function FloatingAddSectionButton() {
  const { incrementSectionCount, decrementSectionCount, sectionCount, width, depth, showBOMDialog, showOrderDialog } = useShelvingStore();
  const { camera, controls } = useThree();

  if (showBOMDialog || showOrderDialog) {
    return null;
  }

  function animateCameraToPosition(targetPosition: THREE.Vector3, targetLookAt: THREE.Vector3) {
    const startPosition = camera.position.clone();
    const startLookAt = controls ? (controls as any).target.clone() : camera.position.clone();
    const duration = 500; // milliseconds
    const startTime = Date.now();

    function animate() {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-in-out function for smooth animation
      const eased = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      camera.position.lerpVectors(startPosition, targetPosition, eased);

      if (controls) {
        const currentTarget = new THREE.Vector3();
        currentTarget.lerpVectors(startLookAt, targetLookAt, eased);
        (controls as any).target.copy(currentTarget);
        (controls as any).update();
      }

      camera.lookAt(targetLookAt);
      camera.updateProjectionMatrix();

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    }

    animate();
  }

  function handleAddSection() {
    incrementSectionCount();

    setTimeout(() => {
      const newSectionCount = sectionCount + 1;

      if (newSectionCount > 4) {
        // Switch to front view and shift camera/target to the right
        // Calculate section width including frame width (same as model positioning)
        const sectionWidthInMeters = (FRAME_WIDTH + width) * CM_TO_M;

        // Calculate shift based on how many sections beyond 4 we have
        // Start with 2 section widths at section 5, then add 1 width per additional section
        const sectionsToShift = newSectionCount === 5 ? 2 : 2 + (newSectionCount - 5);
        const shiftAmount = sectionsToShift * sectionWidthInMeters;

        const frontPosition = new THREE.Vector3(2.5 + shiftAmount, 1.0, 6);
        const frontTarget = new THREE.Vector3(2.5 + shiftAmount, 0.98, 0.5);

        animateCameraToPosition(frontPosition, frontTarget);
      }
    }, 50);
  }

  function handleRemoveSection() {
    const currentSectionCount = sectionCount;
    decrementSectionCount();

    setTimeout(() => {
      const newSectionCount = currentSectionCount - 1;

      if (newSectionCount <= 4) {
        // Go back to initial camera position
        const initialPosition = new THREE.Vector3(-2.5, 0.7, 3.8);
        const initialTarget = new THREE.Vector3(1.2, 0.98, 0.5);

        animateCameraToPosition(initialPosition, initialTarget);
      } else if (newSectionCount > 4) {
        // Calculate section width including frame width (same as model positioning)
        const sectionWidthInMeters = (FRAME_WIDTH + width) * CM_TO_M;

        // Calculate shift based on how many sections beyond 4 we have
        const sectionsToShift = newSectionCount === 5 ? 2 : 2 + (newSectionCount - 5);
        const shiftAmount = sectionsToShift * sectionWidthInMeters;

        const frontPosition = new THREE.Vector3(2.5 + shiftAmount, 1.0, 6);
        const frontTarget = new THREE.Vector3(2.5 + shiftAmount, 0.98, 0.5);

        animateCameraToPosition(frontPosition, frontTarget);
      }
    }, 50);
  }

  const sectionPitch = (width + FRAME_WIDTH) * CM_TO_M; // afstand per sectie incl. frame
  const xPos = 0.3 * width * CM_TO_M + sectionCount * sectionPitch;
  const zPos = -(depth * CM_TO_M) / 2;

  return (
    <Html
      position={[xPos, 1, zPos]}
      distanceFactor={3.5}
      style={{ zIndex: 1, position: 'relative' }}
    >
      <div
        className="flex flex-col items-center space-y-2"
        style={{ zIndex: 1, position: 'relative' }}
      >
        <Button
          size="icon"
          className="h-8 w-8 rounded-full shadow-lg hover:opacity-80 transition-opacity"
          onClick={handleAddSection}
          style={{ ...plusButtonStyle, zIndex: 1, position: 'relative' }}
        >
          <Plus className="h-4 w-4" />
        </Button>

        {sectionCount > 1 && (
          <Button
            size="icon"
            className="h-8 w-8 rounded-full shadow-lg hover:opacity-80 transition-opacity"
            onClick={handleRemoveSection}
            style={{ ...minusButtonStyle, zIndex: 1, position: 'relative' }}
          >
            <Minus className="h-4 w-4" />
          </Button>
        )}
      </div>
    </Html>
  );
}